import React from "react";
import { Captions } from "lucide-react";
import { ContextMenuItem } from "../ui/ContextMenu";
import {
  CONSTRAINT_VISIBILITY_UI_EVENT,
  getConstraintVisibilityUiState,
  setConstraintVisibilityUiState,
} from "../../selected-constraint-visibility/visibilityEvents";
import {
  BODY_VISIBILITY_UI_EVENT,
  getBodyVisibilityUiState,
  setBodyVisibilityUiState,
} from "../../selected-body-visibility";
import {
  JOINT_VISIBILITY_UI_EVENT,
  getJointVisibilityUiState,
  setJointVisibilityUiState,
} from "../../selected-joint-visibility";
import {
  SITE_VISIBILITY_UI_EVENT,
  getSiteVisibilityUiState,
  setSiteVisibilityUiState,
} from "../../selected-site-visibility";
import {
  SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
  getSceneInventoryLabelVisibilityState,
  setSceneInventoryLabelVisibilityState,
} from "../../scene-inventory-labels";

function useConstraintVisibilityUiSnapshot() {
  const [state, setState] = React.useState(() => getConstraintVisibilityUiState());
  React.useEffect(() => {
    const handler = (event) => {
      setState({
        ...getConstraintVisibilityUiState(),
        ...(event?.detail || {}),
      });
    };
    window.addEventListener(CONSTRAINT_VISIBILITY_UI_EVENT, handler);
    return () => window.removeEventListener(CONSTRAINT_VISIBILITY_UI_EVENT, handler);
  }, []);
  return state;
}

function useViewportLabelVisibilitySnapshot() {
  const [state, setState] = React.useState(() => ({
    body: getBodyVisibilityUiState(),
    joint: getJointVisibilityUiState(),
    site: getSiteVisibilityUiState(),
    constraint: getConstraintVisibilityUiState(),
    inventory: getSceneInventoryLabelVisibilityState(),
  }));

  React.useEffect(() => {
    const sync = () => {
      setState({
        body: getBodyVisibilityUiState(),
        joint: getJointVisibilityUiState(),
        site: getSiteVisibilityUiState(),
        constraint: getConstraintVisibilityUiState(),
        inventory: getSceneInventoryLabelVisibilityState(),
      });
    };
    window.addEventListener(BODY_VISIBILITY_UI_EVENT, sync);
    window.addEventListener(JOINT_VISIBILITY_UI_EVENT, sync);
    window.addEventListener(SITE_VISIBILITY_UI_EVENT, sync);
    window.addEventListener(CONSTRAINT_VISIBILITY_UI_EVENT, sync);
    window.addEventListener(SCENE_INVENTORY_LABEL_VISIBILITY_EVENT, sync);
    return () => {
      window.removeEventListener(BODY_VISIBILITY_UI_EVENT, sync);
      window.removeEventListener(JOINT_VISIBILITY_UI_EVENT, sync);
      window.removeEventListener(SITE_VISIBILITY_UI_EVENT, sync);
      window.removeEventListener(CONSTRAINT_VISIBILITY_UI_EVENT, sync);
      window.removeEventListener(SCENE_INVENTORY_LABEL_VISIBILITY_EVENT, sync);
    };
  }, []);

  return state;
}

function setOverlayLayerVisible(kind, visible, showConstraintMarkers, viewerActions) {
  const patch =
    kind === "tendon"
      ? {
          tendonsVisible: !!visible,
          ...(!showConstraintMarkers && visible ? { constraintsVisible: false } : {}),
        }
      : {
          constraintsVisible: !!visible,
          ...(!showConstraintMarkers && visible ? { tendonsVisible: false } : {}),
        };
  setConstraintVisibilityUiState(patch);
  if (visible && !showConstraintMarkers) {
    viewerActions.toggleShowConstraintMarkers();
  }
  const nextState = getConstraintVisibilityUiState();
  if (
    !visible &&
    !nextState.constraintsVisible &&
    !nextState.tendonsVisible &&
    showConstraintMarkers
  ) {
    viewerActions.toggleShowConstraintMarkers();
  }
}

function getViewportLabelConfig(key, labelState) {
  switch (key) {
    case "body":
      return {
        label: "Body",
        visible: !!labelState.body.labels,
        setVisible: (visible) => setBodyVisibilityUiState({ labels: !!visible }),
      };
    case "object":
      return {
        label: "Object",
        visible: !!labelState.body.objectLabels,
        setVisible: (visible) => setBodyVisibilityUiState({ objectLabels: !!visible }),
      };
    case "showVisual":
      return {
        label: "Visual",
        visible: !!labelState.inventory.visual || !!labelState.inventory.mesh,
        setVisible: (visible) =>
          setSceneInventoryLabelVisibilityState({ visual: !!visible, mesh: !!visible }),
      };
    case "showCollision":
      return {
        label: "Collision",
        visible: !!labelState.inventory.collision,
        setVisible: (visible) => setSceneInventoryLabelVisibilityState({ collision: !!visible }),
      };
    case "showJoints":
      return {
        label: "Joint",
        visible: !labelState.joint.labelsOff,
        setVisible: (visible) => setJointVisibilityUiState({ labelsOff: !visible }),
      };
    case "showSites":
      return {
        label: "Site",
        visible: !labelState.site.labelsOff,
        setVisible: (visible) => setSiteVisibilityUiState({ labelsOff: !visible }),
      };
    case "showInertials":
      return {
        label: "Inertial",
        visible: !!labelState.inventory.inertial,
        setVisible: (visible) => setSceneInventoryLabelVisibilityState({ inertial: !!visible }),
      };
    case "showCameras":
      return {
        label: "Camera",
        visible: !!labelState.inventory.camera,
        setVisible: (visible) => setSceneInventoryLabelVisibilityState({ camera: !!visible }),
      };
    case "showConstraintMarkers":
      return {
        label: "Constraint",
        visible: !labelState.constraint.labelsOff,
        setVisible: (visible) => setConstraintVisibilityUiState({ labelsOff: !visible }),
      };
    case "showTendonMarkers":
      return {
        label: "Tendon",
        visible: !labelState.constraint.tendonLabelsOff,
        setVisible: (visible) => setConstraintVisibilityUiState({ tendonLabelsOff: !visible }),
      };
    case "showActuators":
      return {
        label: "Actuator",
        visible: !!labelState.inventory.actuator,
        setVisible: (visible) => setSceneInventoryLabelVisibilityState({ actuator: !!visible }),
      };
    default:
      return null;
  }
}

function LabelVisibilityContextItem({ config, label = "Labels" }) {
  if (!config) return null;
  return (
    <ContextMenuItem
      onSelect={(event) => event.preventDefault()}
      onMouseEnter={(event) => {
        event.currentTarget.style.backgroundColor = "transparent";
        event.currentTarget.style.color = "rgb(229, 231, 235)";
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.backgroundColor = "transparent";
        event.currentTarget.style.color = "rgb(229, 231, 235)";
      }}
      className="justify-between gap-4"
    >
      <span className="flex min-w-0 items-center gap-2">
        <Captions className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        <span className="truncate">{label}</span>
      </span>
      <span
        className="inline-flex shrink-0 rounded-md border border-gray-700 bg-gray-900/30 p-0.5"
        role="group"
        aria-label={`${label} visibility`}
      >
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${
            config.visible ? "bg-gray-700 text-gray-100" : "text-gray-300 hover:text-gray-100"
          }`}
          onClick={(event) => {
            event.stopPropagation();
            config.setVisible(true);
          }}
        >
          On
        </button>
        <button
          type="button"
          className={`rounded px-2 py-1 text-xs ${
            !config.visible ? "bg-gray-700 text-gray-100" : "text-gray-300 hover:text-gray-100"
          }`}
          onClick={(event) => {
            event.stopPropagation();
            config.setVisible(false);
          }}
        >
          Off
        </button>
      </span>
    </ContextMenuItem>
  );
}

export {
  LabelVisibilityContextItem,
  getViewportLabelConfig,
  setOverlayLayerVisible,
  useConstraintVisibilityUiSnapshot,
  useViewportLabelVisibilitySnapshot,
};
